import {
  Badge,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from '@risksmart-app/atomic-ui';
import type * as React from 'react';

export interface IntegrationCardLang {
  getStarted: string;
  comingSoon: string;
  earlyAccess: string;
  contactMessage: string;
}

export interface IntegrationCardProps extends Omit<
  React.ComponentProps<typeof Card>,
  'children' | 'lang'
> {
  name: string;
  description: string;
  content?: string;
  iconUrl: string;
  lang: IntegrationCardLang;
  isComingSoon?: boolean;
  isDisabled?: boolean;
}

function IntegrationCard({
  name,
  description,
  content,
  iconUrl,
  lang,
  isComingSoon,
  isDisabled,
  className,
  ...rest
}: IntegrationCardProps) {
  const isActive = !isComingSoon && !isDisabled;

  const badge = isComingSoon ? (
    <Badge variant={'muted'} size={'md'}>
      {lang.comingSoon}
    </Badge>
  ) : isActive ? (
    <Badge variant={'primary'} size={'md'}>
      {lang.earlyAccess}
    </Badge>
  ) : null;

  return (
    <Card
      className={cn(
        'h-[250px]',
        (isDisabled || isComingSoon) && 'opacity-70',
        isActive && 'cursor-pointer',
        className
      )}
      {...rest}
    >
      <CardHeader>
        <CardTitle className={cn('text-lg')}>
          <div className={cn('flex items-center gap-2')}>
            <img
              src={iconUrl}
              alt={''}
              width={28}
              height={28}
              className={cn('shrink-0')}
            />
            {name}
          </div>
        </CardTitle>
        {badge && <CardAction>{badge}</CardAction>}
        <CardDescription className={cn('text-base text-foreground/70')}>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent
        className={cn(
          'flex-1 overflow-hidden text-base',
          isActive ? 'text-foreground/60' : 'text-neutral-muted-foreground/60'
        )}
      >
        {content}
      </CardContent>
      <CardFooter>
        {isActive && (
          <span className={cn('text-base font-medium text-primary')}>
            {lang.getStarted}
          </span>
        )}
        {isDisabled && (
          <p className={cn('text-xs text-neutral-muted-foreground/60')}>
            {lang.contactMessage}
          </p>
        )}
      </CardFooter>
    </Card>
  );
}

export { IntegrationCard };
